import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CustomButton } from '@/components/molecules/CustomButton';
import { useToast } from '@/hooks/useToastContext';
import { learnMove, type PendingMoveData, type OwnedPokemonAttack } from '@/services/pokemon';

interface LearnMoveModalProps {
  ownedPokemonId: string;
  pokemonName: string;
  pendingMove: PendingMoveData;
  currentAttacks: OwnedPokemonAttack[];
  onDone: () => void;
}

export const LearnMoveModal = ({
  ownedPokemonId,
  pokemonName,
  pendingMove,
  currentAttacks,
  onDone,
}: LearnMoveModalProps) => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<number | null>(null);

  const { mutate: learn, isPending } = useMutation({
    mutationFn: (slotIndex: number) => learnMove(ownedPokemonId, pendingMove.attackId, slotIndex),
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['pokemons'] });
      if (data.learned) {
        showToast(
          'success',
          `${pokemonName} a oublié ${data.forgottenName} et a appris ${data.newAttackName} !`,
        );
      } else {
        showToast('info', `${pokemonName} n'a pas appris ${pendingMove.realname}.`);
      }
      onDone();
    },
    onError: (err: Error) => {
      showToast('error', err.message);
      onDone();
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="w-full max-w-sm overflow-hidden rounded-3xl bg-surface shadow-2xl">
        <div className="bg-secondary-500 px-6 py-4 text-center">
          <p className="font-lexend text-lg font-bold text-white">Nouvelle attaque !</p>
        </div>
        <div className="space-y-4 p-5">
          <div className="text-center">
            <p className="text-content text-sm font-medium">
              {pokemonName} veut apprendre{' '}
              <strong>{pendingMove.realname}</strong> !
            </p>
            <p className="text-content/50 mt-1 text-xs">
              Mais il connaît déjà 4 attaques. Laquelle oublier ?
            </p>
          </div>

          <div className="flex flex-col gap-2">
            {currentAttacks.map((attack, index) => (
              <button
                key={index}
                onClick={() => setSelected(index)}
                disabled={isPending}
                className={`rounded-xl border px-4 py-2.5 text-left text-sm font-medium transition-colors disabled:opacity-50 ${
                  selected === index
                    ? 'border-secondary-500 bg-secondary-500/10 text-secondary-500'
                    : 'border-line-soft bg-raised text-content hover:bg-raised'
                }`}
              >
                {attack.realname}
              </button>
            ))}
          </div>

          <div className="flex gap-3 pt-1">
            <button
              onClick={() => learn(4)}
              disabled={isPending}
              className="text-content/60 flex-1 rounded-full border border-line-soft px-4 py-2 text-sm font-medium transition-colors hover:bg-raised disabled:opacity-50"
            >
              Ne pas apprendre
            </button>
            <CustomButton
              title="Confirmer"
              color="secondary"
              rounded="full"
              disabled={selected === null || isPending}
              loading={isPending}
              onClick={() => selected !== null && learn(selected)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
